// /////////////////////////////////////////////////////////////
// [ファイル] AssistantAuditBadge.tsx
// [概要] AI 回答の非同期検証の結果バッジと、指摘・修正版を示すダイアログ。
//        回答は先に表示され、検証はバックエンド (assistantOnAuditCreated) が裏で行い
//        回答メッセージの audit フィールドへ書き戻す。本コンポーネントはその状態を表示し、
//        「要確認」の場合は指摘内容と修正版の回答を示して、修正版の採用 (本文の差し替え) を受け付ける。
// /////////////////////////////////////////////////////////////
import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Tooltip,
  Typography,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import axios from 'axios';
import * as Icons from '../constants/icons';
import MarkdownMessage from './MarkdownMessage';
import { getIdToken } from '../firebase/getIdToken';
import { useNotification } from '../providers/NotificationProvider';
import { STANDARD_CTA_BUTTON_SX, STANDARD_TEXT_BUTTON_SX } from '../constants/buttonStyles';
import type { AssistantMessage } from '../hooks/useAssistantSessions';

const ADOPT_AUDIT_URL =
  'https://asia-northeast1-plexiform-bot-455507-h3.cloudfunctions.net/adoptAssistantAudit';

interface Props {
  message: AssistantMessage; // id と audit を持つ model 発話
  sessionId: string;
}

export default function AssistantAuditBadge({ message, sessionId }: Props) {
  const audit = message.audit;
  const notify = useNotification();
  const [open, setOpen] = useState(false);
  const [adopting, setAdopting] = useState(false);
  if (!audit || !message.id) return null;

  const chipSx = { height: 20, fontSize: '0.65rem', '& .MuiChip-icon': { fontSize: 14 } } as const;

  if (audit.status === 'pending') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
        <Tooltip title="回答の根拠をツールの実行記録と突き合わせています (数秒〜十数秒)">
          <Chip size="small" variant="outlined" icon={<CircularProgress size={10} />} label="検証中" sx={chipSx} />
        </Tooltip>
      </Box>
    );
  }
  if (audit.status === 'ok') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
        <Tooltip title="回答の事実・出所をツールの実行記録と突き合わせ、問題は見つかりませんでした">
          <Chip size="small" variant="outlined" color="success" icon={<Icons.CheckCircleOutline />} label="検証済み" sx={chipSx} />
        </Tooltip>
      </Box>
    );
  }
  if (audit.status === 'skipped') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
        <Tooltip title="自動検証を実行できませんでした (回答の内容はご自身でご確認ください)">
          <Chip size="small" variant="outlined" label="未検証" sx={chipSx} />
        </Tooltip>
      </Box>
    );
  }

  // status === 'issue'
  const adopted = Boolean(audit.adopted);
  const handleAdopt = async () => {
    setAdopting(true);
    try {
      const token = await getIdToken();
      if (!token) throw new Error('認証情報を取得できませんでした');
      const res = await axios.post(
        ADOPT_AUDIT_URL,
        { sessionId, messageId: message.id },
        { headers: { Authorization: `Bearer ${token}` }, validateStatus: () => true },
      );
      if (res.data?.status !== 'success') {
        throw new Error(res.data?.message || `HTTP ${res.status}`);
      }
      notify.success('修正版の回答を採用しました');
      setOpen(false);
    } catch (e) {
      notify.error(`採用に失敗しました (${e instanceof Error ? e.message : String(e)})`);
    } finally {
      setAdopting(false);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
        <Tooltip title={adopted ? '検証の指摘を反映した修正版に差し替え済みです (クリックで指摘を表示)' : '自動検証で裏付けや出所に疑義が見つかりました (クリックで内容を表示)'}>
          <Chip
            size="small"
            variant={adopted ? 'outlined' : 'filled'}
            color={adopted ? 'default' : 'warning'}
            icon={adopted ? <Icons.CheckCircleOutline /> : <Icons.WarningAmber />}
            label={adopted ? '修正版を採用済み' : '要確認'}
            onClick={() => setOpen(true)}
            sx={{ ...chipSx, cursor: 'pointer' }}
          />
        </Tooltip>
      </Box>
      <Dialog open={open} onClose={() => !adopting && setOpen(false)} maxWidth="md" fullWidth disableScrollLock>
        <DialogTitle sx={{ fontSize: '0.875rem', py: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icons.FactCheck sx={{ fontSize: 18, color: blueGrey[600] }} />
          検証エージェントの監査報告
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            回答の事実・出所を、このターンで実際に実行したツールの記録と突き合わせた結果です。
            {adopted ? '修正版は既に本文へ反映されています。' : '修正版を採用すると、この回答の本文が差し替わります (元の本文は記録に残ります)。'}
          </Typography>
          <Box>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'warning.dark', mb: 0.5 }}>指摘内容</Typography>
            <Box sx={{ p: 1.25, borderRadius: 1, bgcolor: '#fff8e1', border: 1, borderColor: '#ffe082' }}>
              <MarkdownMessage>{audit.issues || '指摘内容を取得できませんでした。'}</MarkdownMessage>
            </Box>
          </Box>
          {audit.corrected && !adopted && (
            <>
              <Divider />
              <Box>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: blueGrey[700], mb: 0.5 }}>修正版の回答 (実行記録にある事実だけで書き直したもの)</Typography>
                <Box sx={{ p: 1.25, borderRadius: 1, bgcolor: '#fafafa', border: 1, borderColor: 'divider' }}>
                  <MarkdownMessage>{audit.corrected}</MarkdownMessage>
                </Box>
              </Box>
            </>
          )}
          {!audit.corrected && !adopted && (
            <Typography variant="caption" color="text.secondary">
              修正版は作成できませんでした。指摘内容を踏まえて、必要なら読み直しを依頼してください。
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={adopting} sx={STANDARD_TEXT_BUTTON_SX}>
            閉じる
          </Button>
          {audit.corrected && !adopted && (
            <Button
              onClick={handleAdopt}
              disabled={adopting}
              startIcon={adopting ? <CircularProgress size={14} color="inherit" /> : <Icons.CheckCircle />}
              sx={STANDARD_CTA_BUTTON_SX}
            >
              修正版を採用する
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
